"use client";

import React from "react";
import { CircleAlert as AlertCircle, ArrowRight, CircleAlert, Droplets, Flame, Hammer, Pipette, Search, ShieldCheck, Sparkles, Wrench, PhoneCall } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
interface ServiceItem {
  id: string;
  title: string;
  description: string;
  badge: string;
  url: string;
  iconName: "alert" | "drain" | "heater" | "leak" | "toilet" | "sewer" | "pipe" | "faucet";
}
const servicesData: ServiceItem[] = [{
  id: "emergency-plumbing",
  title: "Emergency Plumbing",
  description: "Rapid 24/7 response for sudden leaks, bursts, backups, and urgent household plumbing disasters.",
  badge: "24/7 Priority",
  url: "/emergency-plumbing/",
  iconName: "alert"
}, {
  id: "drain-cleaning",
  title: "Drain Cleaning",
  description: "Complete blockage removal and line clearing for sinks, showers, tubs, and main floor drains.",
  badge: "Fast Clearing",
  url: "/drain-cleaning/",
  iconName: "drain"
}, {
  id: "water-heater-repair",
  title: "Water Heater Repair",
  description: "Reliable troubleshooting and fixing for tank and tankless systems experiencing no hot water.",
  badge: "Same-Day Fix",
  url: "/water-heater-repair/",
  iconName: "heater"
}, {
  id: "leak-detection",
  title: "Leak Detection",
  description: "Non-invasive electronic leak locating for concealed pipes inside walls, ceilings, and slabs.",
  badge: "Pinpoint Precision",
  url: "/leak-detection-repair/",
  iconName: "leak"
}, {
  id: "toilet-repair",
  title: "Toilet Repair",
  description: "Immediate resolution for overflowing, constantly running, cracked, or clogged residential toilets.",
  badge: "No More Clogs",
  url: "/toilet-repair/",
  iconName: "toilet"
}, {
  id: "sewer-line-service",
  title: "Sewer Line Service",
  description: "Comprehensive main sewer inspection, root clearing, and damage repairs to stop foul backups.",
  badge: "Main Line Pros",
  url: "/sewer-line-repair/",
  iconName: "sewer"
}, {
  id: "pipe-repair",
  title: "Pipe Repair",
  description: "Durable repair and replacement for frozen, ruptured, corroded, or vibrating household piping.",
  badge: "Long-Term Fix",
  url: "/pipe-repair/",
  iconName: "pipe"
}, {
  id: "faucet-repair",
  title: "Faucet Repair",
  description: "Quick repair of dripping fixtures, damaged cartridges, low pressure, and leaky sink valves.",
  badge: "Save Water",
  url: "/faucet-sink-repair/",
  iconName: "faucet"
}];
function ServiceIcon({
  name
}: {
  name: ServiceItem["iconName"];
}) {
  switch (name) {
    case "alert":
      return <AlertCircle className="size-6 text-accent" />;
    case "drain":
      return <Sparkles className="size-6 text-accent" />;
    case "heater":
      return <Flame className="size-6 text-accent" />;
    case "leak":
      return <Search className="size-6 text-accent" />;
    case "toilet":
      return <Wrench className="size-6 text-accent" />;
    case "sewer":
      return <Droplets className="size-6 text-accent" />;
    case "pipe":
      return <Hammer className="size-6 text-accent" />;
    case "faucet":
      return <Pipette className="size-6 text-accent" />;
    default:
      return <Wrench className="size-6 text-accent" />;
  }
}
export default function ServicesGrid() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="plumbingservicesgrid" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3 px-3 py-1 text-xs font-semibold uppercase tracking-wide">
            Reliable Local Solutions
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Professional Plumbing Services
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">
            Fast, dependable solutions for homeowners and businesses in Jonesboro, GA. From urgent midnight leaks to routine fixture repairs.
          </p>
        </div>

        {/* Services Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {servicesData.map((service, i) => <div key={service.id} data-index={i} className="h-full">
              <motion.div variants={itemVariants} className="h-full">
                <Card className="h-full flex flex-col justify-between border border-border bg-card shadow-sm hover:shadow-md hover:border-accent/40 transition-all group">
                  <div>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/15 transition-colors">
                          <ServiceIcon name={service.iconName} />
                        </div>
                        <Badge variant="outline" className="text-2xs font-medium border-border/80">
                          {service.badge}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl font-bold text-card-foreground group-hover:text-primary transition-colors">
                        {service.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                        {service.description}
                      </CardDescription>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-2 border-t border-border/40 mt-auto">
                    <Button asChild variant="ghost" className="w-full justify-between px-2 text-primary hover:text-primary hover:bg-primary/5 font-semibold group/btn">
                      <Link to={service.url}>
                        <span>Service details</span>
                        <ArrowRight className="size-4 group-hover/btn:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            </div>)}
        </motion.div>

        {/* Bottom CTA Banner */}
        <div className="mt-12 md:mt-16 bg-primary text-primary-foreground rounded-2xl p-6 sm:p-8 md:p-10 shadow-md flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-center md:text-left">
            <div className="inline-flex items-center gap-2 bg-primary-foreground/10 px-3 py-1 rounded-full text-xs font-semibold">
              <ShieldCheck className="size-4 text-accent" />
              <span>Licensed, Bonded & Insured Technicians</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-primary-foreground">
              Facing an urgent plumbing emergency right now?
            </h3>
            <p className="text-primary-foreground/80 text-sm sm:text-base max-w-xl">
              Our dispatch team is ready 24/7 across Jonesboro to send an expert straight to your door.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto shrink-0">
            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold shadow-sm">
              <Link to="/contact/">
                <PhoneCall className="size-4" />
                <span>Request Service Now</span>
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 bg-transparent">
              <Link to="/emergency-plumbing/">
                <span>Emergency Info</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}